import React, { ReactElement, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  Table as MUITable,
  Skeleton,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Popover,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import moment from "moment";

import TablePagination from "../paginations/table-pagination";
import { NoData } from "..";
import { getValue } from "./helpers";
import { ROW_HEIGHT, table_sort } from "@/constants";

const DynamicTable = <T extends IBase>({
  data,
  onRowClick,
  columns,
  isLoading,
  handlePageChange,
  handleLimitChange,
  limit,
  page,
  total,
  hidePagination,
  TotalEstimatedRow,
  stickyHeader,
  renderFooter,
  hideScrollBars,
  isFullHeight,
  square,
  borderLess,
  containerStyles,
  contentIdKey,
  // Sorting props
  sortOrder,
  sortBy,
  onRequestSort,
  // Filter props
  filters,
  onFilterChange,
  ...rest
}: DynamicTableProps<T> & {
  sortOrder?: "asc" | "desc";
  sortBy?: string;
  onRequestSort?: (property: string) => void;
  filters?: Record<string, any>;
  onFilterChange?: (key: string, value: any) => void;
}): ReactElement => {
  const extendedColumns = columns?.map((column) => {
    return { ...column, key: column.key.toString().split(".") };
  });

  const columnMap = useMemo(() => {
    const map: Record<string, any> = {};
    columns?.forEach((column) => {
      map[column.key.toString()] = column;
    });
    return map;
  }, [columns]);

  // Local sort state fallback
  const [localOrder, setLocalOrder] = useState<"asc" | "desc">("asc");
  const [localOrderBy, setLocalOrderBy] = useState<string>("");

  // Filter Popover State
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(null);
  const [tempFilterValue, setTempFilterValue] = useState<string>("");
  const [filterOperator, setFilterOperator] = useState<string>("contains");
  const [multiSelectValue, setMultiSelectValue] = useState<string[]>([]);
  const [hybridTextValue, setHybridTextValue] = useState<string>("");
  const [hybridTextOperator, setHybridTextOperator] = useState<string>("contains");

  const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>, columnKey: string) => {
    setFilterAnchorEl(event.currentTarget);
    setActiveFilterColumn(columnKey);

    const existingFilter = filters?.[columnKey];
    const columnDef = columnMap[columnKey];
    const hasOptions = columnDef && (columnDef as any).filterOptions;
    const allowTextFilter = columnDef && (columnDef as any).allowTextFilter;

    if (hasOptions) {
      setFilterOperator("in");
      if (existingFilter && typeof existingFilter === "object" && Array.isArray(existingFilter.value)) {
        setMultiSelectValue(existingFilter.value);
      } else {
        setMultiSelectValue([]);
      }
      setTempFilterValue("");

      // Initialize hybrid text filter if enabled
      if (allowTextFilter && existingFilter?.textFilter) {
        setHybridTextValue(existingFilter.textFilter.value || "");
        setHybridTextOperator(existingFilter.textFilter.operator || "contains");
      } else {
        setHybridTextValue("");
        setHybridTextOperator("contains");
      }
    } else {
      setMultiSelectValue([]);
      if (existingFilter && typeof existingFilter === "object" && "value" in existingFilter) {
        const rawValue = existingFilter.value;
        setTempFilterValue(rawValue !== undefined && rawValue !== null ? String(rawValue) : "");
        setFilterOperator(existingFilter.operator || "contains");
      } else if (existingFilter !== undefined && existingFilter !== null) {
        setTempFilterValue(String(existingFilter));
        setFilterOperator("contains");
      } else {
        setTempFilterValue("");
        setFilterOperator("contains");
      }
    }
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    setActiveFilterColumn(null);
    setTempFilterValue("");
    setFilterOperator("contains");
    setMultiSelectValue([]);
    setHybridTextValue("");
    setHybridTextOperator("contains");
  };

  const handleFilterApply = () => {
    if (!activeFilterColumn || !onFilterChange) {
      handleFilterClose();
      return;
    }

    const columnDef = columnMap[activeFilterColumn];
    const multiSelectEnabled = Boolean(columnDef && (columnDef as any).filterOptions);

    if (multiSelectEnabled) {
      const allowTextFilter = columnDef && (columnDef as any).allowTextFilter;

      const cleanedValues = multiSelectValue.filter(
        (item) => item !== undefined && item !== null && item !== ""
      );

      const filterPayload: any = {};

      if (cleanedValues.length > 0) {
        filterPayload.value = cleanedValues;
        filterPayload.operator = "in";
      }

      // Add text filter if hybrid mode and text entered
      if (allowTextFilter && hybridTextValue.trim()) {
        filterPayload.textFilter = {
          value: hybridTextValue.trim(),
          operator: hybridTextOperator,
        };
      }

      if (Object.keys(filterPayload).length === 0) {
        onFilterChange(activeFilterColumn, null);
      } else {
        onFilterChange(activeFilterColumn, filterPayload);
      }
    } else {
      const trimmedValue = tempFilterValue.trim();
      if (!trimmedValue) {
        onFilterChange(activeFilterColumn, null);
      } else {
        onFilterChange(activeFilterColumn, { value: trimmedValue, operator: filterOperator });
      }
    }

    handleFilterClose();
  };

  const handleFilterClear = () => {
    if (activeFilterColumn && onFilterChange) {
      onFilterChange(activeFilterColumn, null);
    }
    handleFilterClose();
  };

  const generateCell = <T extends IBase>(
    column: {
      key: string[];
      name: string;
      component?: (data: T, index: number) => React.ReactNode;
      hideEdit?: boolean;
      hideDelete?: boolean;
      align: "left" | "center" | "right";
      sorting?: boolean;
      infoTooltip?: string;
    },
    item: T,
    index: number
  ): React.ReactNode | string => {
    const cell = column.component
      ? column.component(item, index)
      : getValue(item, column.key);

    // Format Date objects to string before rendering
    if (cell instanceof Date) {
      return moment(cell).format("DD-MM-YYYY");
    }

    return cell;
  };

  const handleRequestSort = (property: string) => {
    if (onRequestSort) {
      onRequestSort(property);
    } else {
      const isDesc = localOrderBy === property && localOrder === "desc";
      setLocalOrder(isDesc ? "asc" : "desc");
      setLocalOrderBy(property);
    }
  };

  // Use provided sort props or fallback to local
  const currentOrder = sortOrder || localOrder;
  const currentOrderBy = sortBy || localOrderBy;

  const sortedData = useMemo(() => {
    // If server-side sorting is used (onRequestSort provided), assume data is sorted
    if (onRequestSort) return data;

    if (!currentOrderBy) return data;

    return data.slice().sort((a, b) => {
      const aValue = getValue(a, currentOrderBy.split("."));
      const bValue = getValue(b, currentOrderBy.split("."));

      if (aValue instanceof Date && bValue instanceof Date) {
        if (aValue < bValue) return currentOrder === "asc" ? -1 : 1;
        if (aValue > bValue) return currentOrder === "asc" ? 1 : -1;
        return 0;
      }

      if (aValue < bValue) return currentOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return currentOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, currentOrderBy, currentOrder, onRequestSort]);

  if (data?.length === 0 && !isLoading) {
    return <NoData />;
  }

  const renderSkeletonCells = (rowIndex: number) => {
    return columns.map((column, colIndex) => (
      <TableCell
        key={`skeleton-cell-${rowIndex}-${colIndex}`}
        sx={{
          height: ROW_HEIGHT,
          width: column.width || "auto",
        }}
      >
        <Skeleton variant="rounded" height="100%" width="100%" />
      </TableCell>
    ));
  };
  const renderSkeletonRows = () => {
    return Array.from({ length: limit }, (_, rowIndex) => (
      <TableRow key={`skeleton-row-${rowIndex}`} sx={{ height: "48px" }}>
        {renderSkeletonCells(rowIndex)}
      </TableRow>
    ));
  };

  const activeColumnDef = activeFilterColumn ? columnMap[activeFilterColumn] : null;
  const isMultiSelect = Boolean(
    activeColumnDef && (activeColumnDef as any).filterOptions
  );

  return (
    <Stack
      sx={{
        ...(isFullHeight && { height: "100%", flex: 1 }),
        ...(!square && { borderRadius: "8px" }),
        ...(!borderLess && { border: "1px solid #e6e6e9" }),
        ...(containerStyles && { ...containerStyles }),
      }}
    >
      <TableContainer
        {...(() => {
          const { fillerRowCount, ...restProps } = rest as any;
          return restProps;
        })()}
        className="table"
        sx={{
          ...(hideScrollBars && {
            "&::-webkit-scrollbar": {
              width: 0,
              height: 0,
            },
            ...(square && { borderRadius: 0 }),
          }),
            ...((rest as any).sx as any),
          // Ensure container allows footer to stick to bottom without extra gap
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          paddingBottom: 0,
        }}
      >
        <MUITable sx={{ minWidth: 650, height: "100%" }} stickyHeader={stickyHeader}>
          <TableHead>
            <TableRow>
              {extendedColumns?.map(({ name, key, align, sorting, infoTooltip, width, minWidth }) => (
                <TableCell
                  key={`table-header-${name}-${key}`}
                  align={key[0] === "action" ? "right" : align}
                  sx={{
                    width: width || "auto",
                    minWidth: minWidth || "auto",
                    padding: "8px 12px",
                    fontSize: "14px",
                    fontWeight: 400,
                    lineHeight: "20px",
                    backgroundColor: "#F7F7F7",
                    color: "#5C5C5C",
                    borderBottom: "none",
                  }}
                >
                  <Box
                    display="inline-flex"
                    alignItems="center"
                    justifyContent={key[0] === "action" ? "right" : align}
                    gap={0.5}
                    sx={{
                      width: "100%",
                      flexWrap: "nowrap",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        lineHeight: "20px",
                        color: "inherit",
                        mr: 0.5,
                      }}
                    >
                      {name}
                    </Typography>
                    {infoTooltip && (
                      <Tooltip title={infoTooltip} placement="top">
                        <InfoIcon
                          sx={{
                            color: "#8E9494",
                            fontSize: 16,
                          }}
                        />
                      </Tooltip>
                    )}
                    {sorting && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => handleRequestSort(key.join("."))}
                          sx={{ flexShrink: 0 }}
                        >
                          <img src={table_sort} alt="sort icon" />
                        </IconButton>
                        {onFilterChange && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleFilterClick(e, key.join("."))}
                            sx={{ 
                              color: filters?.[key.join(".")] ? "#0E7090" : "inherit",
                              flexShrink: 0,
                            }}
                          >
                            <FilterAltIcon fontSize="small" />
                          </IconButton>
                        )}
                      </>
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? renderSkeletonRows()
              : sortedData?.map((item, index) => (
                  <TableRow
                    key={`table-content-${(() => {
                      const byContentKey = contentIdKey && (item as any)[contentIdKey];
                      if (byContentKey != null && byContentKey !== "") return String(byContentKey);
                      const byId = (item as any)._id ?? (item as any).id;
                      if (byId != null && byId !== "") return String(byId);
                      return `idx-${index}`;
                    })()}`}
                    sx={{
                      cursor: "pointer",
                      height: "48px",
                      backgroundColor: "#FFFFFF",
                    }}
                    onClick={() => {
                      onRowClick?.(item);
                    }}
                  >
                    {extendedColumns?.map((column) => (
                      <TableCell
                        key={column.key.join(".")}
                        sx={{
                          borderBottom:
                            sortedData?.length - 1 === index
                              ? "none"
                              : "1px solid #EBEBEB",
                          width: column?.width ?? "auto",
                          height: "48px",
                          backgroundColor: "#FFFFFF",
                          color: "#171717",
                          fontSize: "14px",
                          fontWeight: 400,
                          lineHeight: "20px",
                          padding: "12px 20px 12px 12px",
                        }}
                        align={
                          column.key[0] === "action" ? "right" : column.align
                        }
                      >
                        {generateCell(column, item, index)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            {/* Body filler rows to push footer while matching body row background */}
            {!isLoading && (rest as any)?.fillerRowCount
              ? Array.from({ length: Math.max(0, (rest as any).fillerRowCount as number) }).map((_, i) => (
                  <TableRow key={`body-filler-${i}`}>
                    <TableCell colSpan={columns.length} sx={{ height: 48, border: 0 }} />
                  </TableRow>
                ))
              : null}
            {TotalEstimatedRow && (
              <TableRow sx={{ backgroundColor: "#F0F1FF" }}>
                <TableCell colSpan={columns.length}>
                  {TotalEstimatedRow}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {renderFooter?.()}
        </MUITable>
      </TableContainer>
      {!hidePagination && (
        <Box borderTop={"1px solid #e6e6e9"}>
          <TablePagination
            handlePageChange={handlePageChange}
            handleLimitChange={handleLimitChange}
            limit={limit}
            page={page}
            total={total}
          />
        </Box>
      )}

      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box p={2} display="flex" flexDirection="column" gap={2} minWidth={300}>
          <Typography variant="subtitle2" fontWeight={600}>Filter Column</Typography>
          
          {isMultiSelect ? (
            <>
              <FormControl size="small" fullWidth>
                <InputLabel>Select Values</InputLabel>
                <Select
                  multiple
                  value={multiSelectValue}
                  onChange={(e) => {
                    const val = e.target.value;
                    setMultiSelectValue(typeof val === 'string' ? val.split(',') : val);
                  }}
                  input={<OutlinedInput label="Select Values" />}
                  renderValue={(selected) => (selected as string[]).join(', ')}
                  MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                >
                  {((activeColumnDef as any).filterOptions || []).map((option: string) => (
                    <MenuItem key={option} value={option}>
                      <Checkbox checked={multiSelectValue.indexOf(option) > -1} />
                      <ListItemText primary={option} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Text Filter Section (if allowTextFilter is true) */}
              {activeColumnDef && (activeColumnDef as any).allowTextFilter && (
                <>
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                    Or filter by text:
                  </Typography>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Operator</InputLabel>
                    <Select
                      value={hybridTextOperator}
                      label="Operator"
                      onChange={(e) => setHybridTextOperator(e.target.value)}
                    >
                      <MenuItem value="contains">Contains</MenuItem>
                      <MenuItem value="equals">Equals</MenuItem>
                      <MenuItem value="startsWith">Starts With</MenuItem>
                      <MenuItem value="endsWith">Ends With</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    placeholder="Text filter..."
                    value={hybridTextValue}
                    onChange={(e) => setHybridTextValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleFilterApply();
                      }
                    }}
                  />
                </>
              )}
            </>
          ) : (
            <>
              {/* Operator Selection */}
              <FormControl size="small" fullWidth>
                <InputLabel>Operator</InputLabel>
                <Select
                  value={filterOperator}
                  label="Operator"
                  onChange={(e) => setFilterOperator(e.target.value)}
                >
                  <MenuItem value="contains">Contains</MenuItem>
                  <MenuItem value="equals">Equals</MenuItem>
                  <MenuItem value="startsWith">Starts With</MenuItem>
                  <MenuItem value="endsWith">Ends With</MenuItem>
                  <MenuItem value="gt">Greater Than</MenuItem>
                  <MenuItem value="lt">Less Than</MenuItem>
                </Select>
              </FormControl>

              <TextField
                size="small"
                placeholder="Value..."
                value={tempFilterValue}
                onChange={(e) => setTempFilterValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleFilterApply();
                  }
                }}
                autoFocus
              />
            </>
          )}
          
          <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
             <Button size="small" onClick={handleFilterClear}>Clear</Button>
             <Button size="small" variant="contained" onClick={handleFilterApply}>Apply</Button>
          </Box>
        </Box>
      </Popover>
    </Stack>
  );
};

export { DynamicTable };
export { default as VendorsTable } from "./vendors-table";
